import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Switch} from '@/components/ui/switch'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {ListCard, ListEmptyState, SectionCard} from '@/components/section-card'
import {DataTable, type Column} from '@/components/data-table'
import {AnularFacturaDialog, PagoManualDialog} from '@/components/cartera/factura-action-dialogs'
import {facturaActionsColumn, facturaBaseColumns} from '@/components/cartera/factura-columns'
import {EmpresaSstEncenderDialog} from '@/components/cartera/empresa-sst-dialog'
import {EmpresaPrecioSaas} from '@/components/cartera/empresa-precio-saas'
import {PaginatedDataTable} from '@/components/cartera/paginated-data-table'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  useBillingState,
  useCreateSubscription,
  type BillingCycle,
  type BillingSubscriptionEstado,
} from '@/hooks/inci/cartera/use-billing'
import {
  CARTERA_PAGE_SIZE,
  SUBSCRIPTION_ESTADO_TONE,
  useAutoChargeAttempts,
  useCarteraFacturas,
  useResolverPrecio,
  useToggleSst,
  type AdminInvoice,
  type AutoChargeAttempt,
} from '@/hooks/inci/cartera/use-cartera'
import {formatCopFull} from '@/lib/money'

const dateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

function Eyebrow({children}: {children: React.ReactNode}) {
  return (
    <p className="mb-2.5 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
      {children}
    </p>
  )
}

/** Único sello de plan: sin suscripción lee el plan; con suscripción, su estado. Nunca los dos. */
type PlanStatusKind = 'free' | 'paid_no_subscription' | BillingSubscriptionEstado

function planStatusKind(
  planTier: 'free' | 'paid',
  subscription: {estado: BillingSubscriptionEstado} | null
): PlanStatusKind {
  if (subscription) return subscription.estado
  return planTier === 'paid' ? 'paid_no_subscription' : 'free'
}

const PLAN_STATUS_TONE: Record<PlanStatusKind, 'green' | 'amber' | 'red' | 'neutral'> = {
  free: 'neutral',
  paid_no_subscription: 'green',
  ...SUBSCRIPTION_ESTADO_TONE,
}

function planStatusLabel(t: ReturnType<typeof useTranslation>['t'], kind: PlanStatusKind): string {
  if (kind === 'free') return t('plans.tier.free')
  if (kind === 'paid_no_subscription') return t('plans.tier.paid')
  return t(`cartera.estado.${kind}`)
}

/** Una sola acción primaria según el estado. Pro sin costo es una suscripción real con precio
 * propio en $0 pagada manualmente; crear usa el endpoint del tenant (el admin opera en god mode). */
export function PlanYSuscripcion({
  businessId,
  segmentKey,
  planTier,
}: {
  businessId: string
  segmentKey: string | null
  planTier: 'free' | 'paid'
}) {
  const {t} = useTranslation()
  const {data: state, isLoading} = useBillingState(businessId)
  const createSubscription = useCreateSubscription(businessId)
  const toggleSst = useToggleSst()
  const [cycle, setCycle] = useState<BillingCycle>('mensual')
  const [sstDialogOpen, setSstDialogOpen] = useState(false)
  const [sstOffTarget, setSstOffTarget] = useState(false)
  const [pagoManualTarget, setPagoManualTarget] = useState<AdminInvoice | null>(null)

  const subscription = state?.subscription ?? null
  const sstItem = subscription?.items.find((item) => item.item_kind === 'sst')
  const subscriptionCycle = (subscription?.billing_cycle ?? cycle) as BillingCycle
  const kind = planStatusKind(planTier, subscription)
  const needsPago = kind === 'pendiente_pago' || kind === 'en_gracia' || kind === 'degradada'

  const sstResolver = useResolverPrecio({
    segmentKey,
    billingCycle: subscriptionCycle,
    businessId,
    itemKind: 'sst',
    enabled: !!segmentKey,
  })

  const {data: latestInvoices} = useCarteraFacturas(
    {page: 1, search: '', estado: '', payerKind: '', businessId},
    needsPago
  )
  const pendingInvoice = latestInvoices?.items.find((inv) => inv.estado === 'emitida' || inv.estado === 'vencida')

  if (isLoading || !state) return <Skeleton className="h-40 w-full" />

  return (
    <SectionCard
      title={t('cartera.empresaPanel.planTitle')}
      actions={
        <Tag tone={PLAN_STATUS_TONE[kind]} data-testid="empresa-panel-plan-estado">
          {planStatusLabel(t, kind)}
        </Tag>
      }
      bodyClassName="space-y-4 px-5 py-4"
      testId="empresa-panel-plan">
      <div>
        <Eyebrow>{t('cartera.empresaPanel.saasPriceTitle')}</Eyebrow>
        <EmpresaPrecioSaas businessId={businessId} segmentKey={segmentKey} billingCycle={subscriptionCycle} />
      </div>

      {subscription && (
        <div className="surface-divider space-y-2 border-t pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">{t(`cartera.precios.${subscription.billing_cycle}`)}</span>
            <span className="font-mono text-xs text-muted-foreground">
              {dateFormat.format(new Date(subscription.period_start))} –{' '}
              {dateFormat.format(new Date(subscription.period_end))}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="empresa-panel-auto-debito">
            <span className="font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t('cartera.empresaPanel.autoDebito.label')}
            </span>
            {state.payment_source ? (
              // El medio de pago es identidad, no estado (U2(a)): el estado de cobro va en su propio chip.
              <Tag tone="neutral">
                {state.payment_source.tipo === 'nequi'
                  ? t('cartera.empresaPanel.autoDebito.nequi', {last4: state.payment_source.last4 ?? ''})
                  : t('cartera.empresaPanel.autoDebito.card', {
                      brand: state.payment_source.brand ?? 'Tarjeta',
                      last4: state.payment_source.last4 ?? '',
                    })}
              </Tag>
            ) : (
              <Tag tone="neutral">{t('cartera.empresaPanel.autoDebito.sin')}</Tag>
            )}
          </div>
        </div>
      )}

      <div className="surface-divider border-t pt-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-foreground">{t('cartera.empresaPanel.sstToggleLabel')}</p>
            {sstResolver.data && (
              <p className="font-mono text-xs text-muted-foreground tabular-nums">
                {t('cartera.empresaPanel.sstDialogVigente', {amount: formatCopFull(sstResolver.data.amount_cop)})}
              </p>
            )}
          </div>
          <Switch
            checked={!!sstItem}
            disabled={!subscription}
            data-testid="empresa-panel-sst-toggle"
            aria-label={t('cartera.empresaPanel.sstToggleLabel')}
            onCheckedChange={(checked) => {
              if (!subscription) return
              if (checked) setSstDialogOpen(true)
              else setSstOffTarget(true)
            }}
          />
        </div>
      </div>

      {(!subscription || needsPago) && (
        <div className="surface-divider border-t pt-4">
          {!subscription && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                data-testid="empresa-panel-crear-ciclo"
                value={cycle}
                onChange={(e) => setCycle(e.target.value as BillingCycle)}
                className="h-9 rounded-md border border-input bg-transparent px-2 text-sm text-foreground">
                <option value="mensual">{t('cartera.precios.mensual')}</option>
                <option value="anual">{t('cartera.precios.anual')}</option>
              </select>
              <Button
                data-testid="empresa-panel-crear-suscripcion"
                disabled={createSubscription.isPending}
                onClick={() => createSubscription.mutate({billing_cycle: cycle})}>
                {createSubscription.isPending ? t('common.saving') : t('cartera.empresaPanel.crearSuscripcion')}
              </Button>
            </div>
          )}
          {needsPago && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                data-testid="empresa-panel-marcar-pago-manual"
                disabled={!pendingInvoice}
                onClick={() => pendingInvoice && setPagoManualTarget(pendingInvoice)}>
                {t('cartera.empresaPanel.marcarPagoManual')}
              </Button>
            </div>
          )}
        </div>
      )}

      {subscription && segmentKey && (
        <EmpresaSstEncenderDialog
          open={sstDialogOpen}
          onOpenChange={setSstDialogOpen}
          businessId={businessId}
          subscriptionId={subscription.id}
          segmentKey={segmentKey}
          billingCycle={subscriptionCycle}
          currentSstPrice={sstResolver.data}
        />
      )}
      <ConfirmDialog
        open={sstOffTarget}
        onOpenChange={setSstOffTarget}
        title={t('cartera.suscripciones.sstToggleOffTitle')}
        description={t('cartera.suscripciones.sstToggleOffDescription', {empresa: ''})}
        confirmLabel={t('cartera.suscripciones.sstToggleConfirm')}
        cancelLabel={t('common.cancel')}
        pending={toggleSst.isPending}
        onConfirm={() => {
          if (!subscription) return
          toggleSst.mutate({subscriptionId: subscription.id, incluir: false}, {onSettled: () => setSstOffTarget(false)})
        }}
      />
      <PagoManualDialog
        target={pagoManualTarget}
        onClose={() => setPagoManualTarget(null)}
        testPrefix="empresa-panel-plan"
      />
    </SectionCard>
  )
}

export function EmpresaFacturas({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const table = useTableState('created_at')
  const {data, isLoading} = useCarteraFacturas({page: table.page, search: '', estado: '', payerKind: '', businessId})
  const [anularTarget, setAnularTarget] = useState<AdminInvoice | null>(null)
  const [pagoManualTarget, setPagoManualTarget] = useState<AdminInvoice | null>(null)

  const columns: Column<AdminInvoice>[] = [
    ...facturaBaseColumns(t),
    facturaActionsColumn(t, setPagoManualTarget, setAnularTarget),
  ]

  return (
    <div data-testid="empresa-panel-facturas">
      {/* Sin `title`: la pestaña activa ya dice «Facturas» (T3). */}
      <PaginatedDataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        page={table.page}
        onPageChange={table.setPage}
        emptyMessage={t('cartera.facturas.empty')}
      />
      <AnularFacturaDialog target={anularTarget} onClose={() => setAnularTarget(null)} testPrefix="cartera" />
      <PagoManualDialog target={pagoManualTarget} onClose={() => setPagoManualTarget(null)} testPrefix="cartera" />
    </div>
  )
}

/** Solo cargos automáticos (auto-débito spec, decisión 4). Se pinta siempre, con su vacío propio. */
export function EmpresaIntentosCargo({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data} = useAutoChargeAttempts(businessId, page)
  const rows = data?.items ?? []

  const columns: Column<AutoChargeAttempt>[] = [
    {
      key: 'factura',
      header: t('cartera.empresaPanel.intentos.factura'),
      cell: (row) => <span className="font-mono text-sm font-medium text-foreground">{row.numero_factura}</span>,
    },
    {
      key: 'intento',
      header: t('cartera.empresaPanel.intentos.intento'),
      width: 'w-20',
      cell: (row) => <span className="font-mono text-sm text-muted-foreground">{row.intento_n ?? '—'}</span>,
    },
    {
      key: 'estado',
      header: t('cartera.empresaPanel.intentos.estado'),
      width: 'w-32',
      cell: (row) => (
        <CeldaEstado
          chip={
            <Tag tone={row.estado === 'aprobado' ? 'green' : row.estado === 'rechazado' ? 'red' : 'amber'}>
              {t(`cartera.empresaPanel.intentos.pago.${row.estado}`)}
            </Tag>
          }
        />
      ),
    },
    {
      key: 'fecha',
      header: t('cartera.empresaPanel.intentos.fecha'),
      width: 'w-32',
      cell: (row) => (
        <span className="font-mono text-xs text-muted-foreground">{dateFormat.format(new Date(row.created_at))}</span>
      ),
    },
    {
      key: 'total',
      header: t('cartera.empresaPanel.intentos.total'),
      width: 'w-32',
      className: 'text-right',
      cell: (row) => (
        <span className="font-mono text-sm text-foreground tabular-nums">{formatCopFull(row.amount_cop)}</span>
      ),
    },
  ]

  return (
    <ListCard
      title={t('cartera.empresaPanel.intentos.title')}
      testId="empresa-panel-intentos-cargo"
      footer={
        !!data?.total && (
          <PaginationBar page={page} pageSize={CARTERA_PAGE_SIZE} total={data.total} onPageChange={setPage} />
        )
      }>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        emptyState={<ListEmptyState title={t('cartera.empresaPanel.intentos.empty')} />}
      />
    </ListCard>
  )
}
